/**
 * downloadTemplate — exports an html_css_workspace section as the four files a
 * committed page template is made of, packaged as <slug>.zip.
 *
 *   index.html    the section's markup
 *   style.css     its CSS
 *   script.js     its JS
 *   config.json   everything the client renderer needs that isn't markup
 *
 * The developer drops the folder into `arcee-client/public/pages/<slug>/` and
 * adds the slug to `src/localPages.js`; the client then renders that page from
 * disk instead of calling the public page API.
 *
 * Everything happens in the browser — no server endpoint, no build step. The
 * editor already holds the html/css/js, so there is nothing to fetch.
 */

import { buildZip } from './zip';
import { safeFileName, saveBlob } from './saveFile';

/** A page that cannot be represented by four files, and why. */
export function templateBlockedReason(page, sectionCount) {
  if (!page?.slug) {
    return 'This page has no slug yet. Save the page first, then download.';
  }
  if (page.visibility && page.visibility !== 'public') {
    // The sign-in redirect for a private page lives in the API response. A
    // static file cannot reproduce it, so exporting one would publish private
    // content to anyone who types the URL.
    return 'Only public pages can be downloaded as templates. This page is private.';
  }
  if (typeof sectionCount === 'number' && sectionCount > 1) {
    // Four files describe one section. Exporting a multi-section page would
    // silently drop everything but this block.
    return `This page has ${sectionCount} sections. A template can only hold a single HTML/CSS section.`;
  }
  return null;
}

/**
 * The four files, as plain objects. Takes the whole section because the export
 * needs both its section_config and its data_binding. Pure, so it can be
 * asserted on directly.
 */
export function buildTemplateFiles(section = {}, page = {}) {
  const cfg = section.section_config || {};
  const config = {
    slug: page.slug,
    name: page.name || page.slug,
    visibility: 'public',
    render_mode: cfg.render_mode || 'inline',
    seo: page.seo || {},
    show_header: page.page_config?.show_header !== false,
    // Without these every link and button in the markup is dead: the client's
    // iframe is sandboxed without allow-same-origin, so it cannot navigate the
    // app itself — the parent resolves the destination from this config.
    link_navigations: cfg.link_navigations || [],
    cards: cfg.cards || cfg.card_buttons || [],
    // The client uses this as the cache-busting version for the other three
    // files, so it has to change whenever they do.
    // A bound section resolves table records at render time. Those requests
    // still happen from a local template, but omitting the binding would
    // render the section's grid empty — a wrong page is worse than a request.
    data_binding: section.data_binding || null,
    downloaded_at: new Date().toISOString(),
    source_page_id: page.id ?? null,
  };
  if (page.page_config?.header) config.header = page.page_config.header;
  if (page.page_config?.footer) config.footer = page.page_config.footer;

  return [
    { name: 'index.html',  content: cfg.html || '',            type: 'text/html' },
    { name: 'style.css',   content: cfg.css  || '',            type: 'text/css' },
    { name: 'script.js',   content: cfg.js   || '',            type: 'text/javascript' },
    { name: 'config.json', content: JSON.stringify(config, null, 2), type: 'application/json' },
  ];
}

/**
 * A slug is a path — "my-home/test-world" is a real, two-segment page — so it
 * cannot go straight into a filename. Folder separators become dashes for the
 * archive's own name, while the paths INSIDE it keep the slug intact so the
 * zip extracts to public/pages/my-home/test-world/ directly.
 */
export function zipFileName(slug) {
  return safeFileName(slug, '.zip');
}

/**
 * Package the four files under a folder named for the slug and save the
 * archive.
 *
 * One zip rather than four loose files because four downloads all called
 * index.html / style.css / script.js / config.json are indistinguishable in a
 * Downloads folder — export two pages and you get index(1).html with no way to
 * tell which page it belongs to. The archive is named for the page, and the
 * files inside keep the exact names the client loader looks for.
 */
export function downloadTemplateZip(files, slug) {
  const bytes = buildZip(files.map((f) => ({ name: `${slug}/${f.name}`, content: f.content })));
  const zip = new Blob([bytes], { type: 'application/zip' });
  saveBlob(zip, zipFileName(slug));
}
